/* Organisaties (/crm/organisaties): één compacte, doorzoekbare lijst, zoals de
 * projectlijst en Locaties (ADR 0022).
 *
 * Tabs: alle, met project, Charging, leveranciers. Per organisatie: naam met sector en
 * plaats, status, projecten, contactpersonen en wanneer hij voor het laatst is
 * bijgewerkt. Bewerken naast de rij; archiveren alleen voor beheerders. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { datum } from '@/features/projectpagina/ui/opmaak';
import {
  beginRichting, filterEnSorteer, filterNaarUrl, organisatieLijst, tellingen, TAB_LABEL,
  type LijstFilter, type LijstOrganisatie, type Sortering,
} from './data/organisaties';
import { archiveerBezwaar } from './data/archiveren';
import { statusLabel, STATUS_KLEUR } from './data/labels';
import { LijstStaat, OrgIcoon, SorteerKop, Tabs, useLijstToetsen, Voet, Werkbalk } from './ui/Lijst';
import { Archiveer } from './ui/Archiveer';
import { Pil } from './ui/Onderdelen';

const KOLOMMEN =
  'grid-cols-[minmax(0,1fr)_92px] md:grid-cols-[minmax(0,1fr)_92px_140px] lg:grid-cols-[minmax(0,1fr)_92px_140px_180px] xl:grid-cols-[minmax(0,1fr)_92px_160px_200px_84px]';

interface Props {
  organisaties: LijstOrganisatie[];
  laden: boolean;
  fout: Error | null;
  filter: LijstFilter;
  onFilter: (f: LijstFilter) => void;
  magBewerken: boolean;
  magBeheren: boolean;
  onNieuw: () => void;
  onBewerk: (o: LijstOrganisatie) => void;
  onArchiveer: (o: LijstOrganisatie) => void;
}

function Projecten({ o }: { o: LijstOrganisatie }) {
  if (!o.projecten.length) return <span className="text-muted-foreground">—</span>;
  const eerste = o.projecten.slice(0, 2);
  return (
    <span className="truncate" title={o.projecten.map((p) => [p.nummer, p.naam].filter(Boolean).join(' ')).join('\n')}>
      <span className="font-mono text-xs">{eerste.map((p) => p.nummer ?? p.naam).join(', ')}</span>
      {o.projecten.length > 2 && <span className="text-muted-foreground"> +{o.projecten.length - 2}</span>}
    </span>
  );
}

function Contacten({ o }: { o: LijstOrganisatie }) {
  if (!o.contacten.length) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="truncate">
      {o.contacten[0]!.naam}
      {o.contacten.length > 1 && <span className="text-muted-foreground"> +{o.contacten.length - 1}</span>}
    </span>
  );
}

export function OrganisatieLijst(props: Props) {
  const { organisaties, laden, fout, filter, onFilter } = props;
  const navigate = useNavigate();
  const zoekRef = useRef<HTMLInputElement>(null);
  const rijRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [actief, setActief] = useState(-1);

  const zichtbaar = useMemo(() => filterEnSorteer(organisaties, filter), [organisaties, filter]);
  const tabs = useMemo(() => tellingen(organisaties, filter.zoek).map((t) => ({ ...t, label: TAB_LABEL[t.tab] })), [organisaties, filter.zoek]);

  useEffect(() => setActief(-1), [filter.zoek, filter.tab]);
  useEffect(() => {
    if (actief >= 0) rijRefs.current[actief]?.scrollIntoView({ block: 'nearest' });
  }, [actief]);
  useEffect(() => {
    if (!laden) organisatieLijst.bewaar(new URLSearchParams(filterNaarUrl(filter)).toString(), zichtbaar.map((o) => o.id));
  }, [zichtbaar, laden, filter]);

  const open = useCallback((i: number) => {
    const o = zichtbaar[i];
    if (o) navigate(`/crm/organisaties/${o.id}`);
  }, [zichtbaar, navigate]);
  const wis = useCallback(() => onFilter({ ...filter, zoek: '' }), [filter, onFilter]);
  useLijstToetsen({ zoekRef, aantal: zichtbaar.length, actief, setActief, open, wis });

  const sorteer = (s: Sortering) =>
    onFilter({ ...filter, sortering: s, oplopend: filter.sortering === s ? !filter.oplopend : beginRichting(s) });
  const kop = { huidig: filter.sortering, oplopend: filter.oplopend, onSorteer: sorteer };
  const acties = props.magBewerken;

  return (
    <div className="flex flex-col gap-4">
      <Werkbalk
        ref={zoekRef}
        titel="Organisaties"
        zoek={filter.zoek}
        onZoek={(zoek) => onFilter({ ...filter, zoek })}
        placeholder="Zoek organisatie, contact of project"
        nieuw={props.magBewerken ? { label: 'Nieuwe organisatie', onKlik: props.onNieuw } : null}
      />
      <Tabs tabs={tabs} label="Soort" actief={filter.tab} onKies={(tab) => onFilter({ ...filter, tab })}
        stip={{ projecten: 'bg-green-600', charging: 'bg-sky-500', leveranciers: 'bg-violet-500' }} />

      {fout || laden || zichtbaar.length === 0 ? (
        <LijstStaat
          fout={fout}
          laden={laden}
          wat="Organisaties"
          leeg={
            <>
              {organisaties.length === 0 ? 'Nog geen organisaties.' : 'Geen organisaties gevonden.'}{' '}
              {(filter.zoek || filter.tab !== 'alle') && organisaties.length > 0 && (
                <button type="button" className="font-medium text-foreground underline" onClick={() => onFilter({ ...filter, zoek: '', tab: 'alle' })}>
                  Toon alles
                </button>
              )}
            </>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-background">
          <div className="flex items-center border-b bg-muted/40 text-xs text-muted-foreground">
            <div className={cn('grid flex-1 items-center gap-3 py-2 pl-4 pr-2', KOLOMMEN)}>
              <SorteerKop s="naam" {...kop}>Organisatie</SorteerKop>
              <span>Status</span>
              <span className="hidden md:block"><SorteerKop s="projecten" {...kop}>Projecten</SorteerKop></span>
              <span className="hidden lg:block">Contactpersonen</span>
              <span className="hidden xl:block"><SorteerKop s="bijgewerkt" {...kop}>Bijgewerkt</SorteerKop></span>
            </div>
            {acties && <span className="w-[76px] shrink-0" />}
          </div>
          <ul className="divide-y">
            {zichtbaar.map((o, i) => {
              const meta = [o.sector, o.plaats, o.classificatie === 'leverancier' ? 'Leverancier' : null, o.charging ? 'Charging' : null].filter(Boolean);
              return (
                <li key={o.id} data-org-rij={o.id} className={cn('group flex items-center', i === actief && 'bg-muted')}>
                  <Link
                    ref={(el) => (rijRefs.current[i] = el)}
                    to={`/crm/organisaties/${o.id}`}
                    onMouseEnter={() => setActief(i)}
                    aria-label={`${o.naam} openen`}
                    className={cn('grid flex-1 items-center gap-3 py-2.5 pl-4 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', KOLOMMEN)}
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <OrgIcoon src={o.icoon} naam={o.naam} />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{o.naam}</span>
                        {meta.length > 0 && <span className="truncate text-xs text-muted-foreground">{meta.join(' · ')}</span>}
                      </span>
                    </span>
                    <span><Pil className={STATUS_KLEUR[o.status ?? ''] ?? 'bg-muted'}>{statusLabel(o.status)}</Pil></span>
                    <span className="hidden min-w-0 md:block"><Projecten o={o} /></span>
                    <span className="hidden min-w-0 lg:block"><Contacten o={o} /></span>
                    <span className="hidden text-muted-foreground xl:block" title={o.bijgewerkt ?? o.aangemaakt}>{datum(o.bijgewerkt ?? o.aangemaakt)}</span>
                  </Link>
                  {acties && (
                    <span className="flex w-[76px] shrink-0 items-center justify-end gap-0.5 pr-2 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                      <Button variant="ghost" size="icon" aria-label="Bewerken" title="Bewerken" className="h-8 w-8 text-muted-foreground" onClick={() => props.onBewerk(o)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {props.magBeheren && (
                        <Archiveer
                          wat="organisatie"
                          naam={o.naam}
                          bezwaar={archiveerBezwaar(o.projecten.length)}
                          uitleg={o.contacten.length ? `De ${o.contacten.length === 1 ? 'contactpersoon blijft' : `${o.contacten.length} contactpersonen blijven`} staan in Contacten.` : undefined}
                          onArchiveer={() => props.onArchiveer(o)}
                        />
                      )}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <Voet aantal={zichtbaar.length} enkelvoud="organisatie" meervoud="organisaties" />
        </div>
      )}
    </div>
  );
}

